import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { NamedLink } from '../../../../components';

import css from './SectionOurStory.module.css';

/**
 * SectionOurStory presents the founder story and core mission on the homepage.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.sectionId] anchor id for linking
 * @param {string} [props.className] additional class names
 * @param {string} [props.rootClassName] override root class name
 * @returns {JSX.Element}
 */
const SectionOurStory = props => {
  const { sectionId = 'our-story', className, rootClassName } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <section id={sectionId} className={classes} aria-labelledby="our-story-heading">
      <div className={css.content}>
        <div className={css.header}>
          <span className={css.eyebrow}>
            <FormattedMessage id="SectionOurStory.eyebrow" defaultMessage="Built by Riders, for Riders" />
          </span>
          <h2 id="our-story-heading" className={css.heading}>
            <FormattedMessage
              id="SectionOurStory.heading"
              defaultMessage="Our {story}"
              values={{
                story: (
                  <span className={css.headingAccent}>
                    <FormattedMessage id="SectionOurStory.headingAccent" defaultMessage="Story" />
                  </span>
                ),
              }}
            />
          </h2>
          <div className={css.storyText}>
            <p>
              We're riders too. When we went looking for used emotos and parts, we found the same
              frustration you probably have: listings scattered across Facebook groups, sketchy forum threads,
              and general marketplaces that don't know a Light Bee from an Ultra Bee, or a Sting from a Varg.
            </p>
            <p>
              Started by a dedicated group of <span className={css.highlightText}>local riders and e-moto enthusiasts</span> in
              Southern California, UsedEmoto was created to give the electric dirt bike
              community a trustworthy, dedicated home. No spam, no scams, and no guessing games about battery
              health, motor power, or title status.
            </p>
            <p>
              Whether you're hitting local trails, building a custom mod, or parting out spares, this platform
              is built around real emoto specs and safe local meetups. Thank you for riding with us.
            </p>
          </div>
        </div>

        <div className={css.grid}>
          <div className={css.card}>
            <div className={css.cardIconWrapper} aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <h3 className={css.cardTitle}>Real Emoto Specs</h3>
            <p className={css.cardDescription}>
              Battery health (%), capacity (Wh), motor power (kW), cycle count, and VIN status. Structured
              specs that actually tell you what you're buying.
            </p>
          </div>

          <div className={css.card}>
            <div className={css.cardIconWrapper} aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <h3 className={css.cardTitle}>Safe Local Meetups</h3>
            <p className={css.cardDescription}>
              Connect directly with verified local riders. Meet in public places, inspect the bike in person,
              check the VIN, and test ride before any payment changes hands.
            </p>
          </div>

          <div className={css.card}>
            <div className={css.cardIconWrapper} aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <h3 className={css.cardTitle}>Fair Price Guidance</h3>
            <p className={css.cardDescription}>
              Integrated fair market valuation estimates so buyers and sellers can agree on realistic prices
              without lowball chaos or guesswork.
            </p>
          </div>
        </div>

        <div className={css.actions}>
          <NamedLink name="SearchPage" className={css.primaryBtn}>
            <FormattedMessage id="SectionOurStory.browseBtn" defaultMessage="Explore listings" />
          </NamedLink>
          <NamedLink name="NewListingPage" className={css.secondaryBtn}>
            <FormattedMessage id="SectionOurStory.sellBtn" defaultMessage="Sell your emoto" />
          </NamedLink>
          <NamedLink name="SafetyPage" className={css.safetyLink}>
            <FormattedMessage id="SectionOurStory.safetyLink" defaultMessage="Trust & Safety guide →" />
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

SectionOurStory.propTypes = {
  sectionId: PropTypes.string,
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default SectionOurStory;
